import * as THREE from 'three';
import { PARK_HOMES, type ParkHomeId } from '../content/parkHomes';
import { Random } from '../core/Random';
import {
  bed,
  bigRecliner,
  crtTv,
  deerMount,
  dogsPokerPainting,
  dresser,
  homeFridge,
  interiorWindow,
  kitchenCounter,
  neonBeerSign,
  rug,
  singingBass,
} from './builders/furniture';
import { bike, cooler, couch, mattress, recliner } from './builders/props';
import type { CollisionWorld } from './Collision';
import type { Vec2 } from './layout';
import { box, cyl, M, mat, mesh, sphere, texMat } from './materials';
import { partition, poster } from './neighborRooms';
import { PublicRoom, type RoomKit } from './PublicRoom';
import { windowPaneMaterial } from './Room';
import { linoleumTexture, panelingTexture, plaidTexture, shagTexture, woodTexture } from './textures';

/**
 * Inside the neighbors' trailers: Tammy Lynn's, Big Earl's, Cousin Dale's, Old Man
 * Jenkins', Skeeter's and Meemaw Pearl's. Single-wide rooms (11.6 x 3.6, the door at
 * local x = 4 on the +z wall), each furnished like the person who lives there.
 */

/** Where you stand to use each thing (room-local), keyed by what it is. */
export const PARK_HOME_SPOTS = {
  tammy: { diffuser: { x: -1.3, z: -0.75 }, board: { x: 0.2, z: -0.35 }, stock: { x: 4.1, z: -0.65 } },
  earl: { recliner: { x: 0.6, z: -0.95 }, fridge: { x: 5.2, z: -0.6 }, bass: { x: -1.0, z: -1.0 } },
  dale: { cushions: { x: 0.4, z: -0.5 }, plans: { x: -1.6, z: -1.0 } },
  jenkins: { photos: { x: 0.0, z: -0.25 }, clock: { x: -1.6, z: -0.85 } },
  skeeter: { upload: { x: 0.6, z: -0.45 }, cans: { x: 2.5, z: -0.8 } },
  meemaw: { candy: { x: 1.9, z: -0.75 }, grandkids: { x: 4.95, z: 0.2 }, dolls: { x: -1.2, z: -1.0 } },
} as const satisfies Record<ParkHomeId, Record<string, Vec2>>;

export interface ParkHomeRoom {
  room: PublicRoom;
  /** The things you use, for outlining. */
  things: Partial<Record<string, THREE.Object3D>>;
  /** Sets something off: the bass sings, the ring light flashes. */
  poke(what: string): void;
}

const D = 3.6;
const BACK = -D / 2;
const FRONT = D / 2;

/** On the back wall, `y` up, facing into the room. */
function onBack(kit: RoomKit, obj: THREE.Object3D, x: number, y: number, inset = 0.03): void {
  obj.position.set(x, y, BACK + inset);
  kit.walls.back.add(obj);
}

/** On the inside of the front wall, facing into the room. */
function onFront(kit: RoomKit, obj: THREE.Object3D, x: number, y: number, inset = 0.03): void {
  obj.position.set(x, y, FRONT - inset);
  obj.rotation.y = Math.PI;
  kit.walls.front.add(obj);
}

function window_(
  kit: RoomKit,
  x: number,
  curtain: string,
  pane: THREE.Material = windowPaneMaterial(),
): void {
  onBack(kit, interiorWindow(pane, curtain), x, 0, 0.025);
}

/** The bedroom at the west end: a half wall, a bed against the end wall, a dresser. */
function bedroom(kit: RoomKit, wall: THREE.Material, blanket: string): void {
  kit.add(partition(1.6, wall), -2.4, -1.0);
  kit.solid(-2.4, -1.0, 0.12, 1.6);
  kit.add(bed(blanket), -4.6, -0.75, Math.PI / 2);
  kit.solid(-4.6, -0.75, 2.2, 1.6);
  kit.add(dresser(), -3.4, 1.5, Math.PI);
  kit.solid(-3.4, 1.5, 1.1, 0.5);
}

function label(text: string, bg: string, fg: string, w: number, h: number, size = 60): THREE.Mesh {
  return poster(
    { w: 256, h: Math.round((256 * h) / w), bg, fg, lines: [{ text, size, font: 'Anton' }] },
    w,
    h,
  );
}

/** A cardboard box with writing on the front. */
function carton(text: string, w: number, h: number, d: number, color = '#b08a5a'): THREE.Group {
  const g = new THREE.Group();
  g.add(box(w, h, d, mat(color, { rough: 1 }), 0, h / 2, 0));
  const l = label(text, '#c8a678', '#2a1a10', w * 0.86, h * 0.5, 48);
  l.position.set(0, h / 2, d / 2 + 0.006);
  g.add(l);
  return g;
}

/** A light whose glow you can animate (its own material, never shared). */
function glow(color: string, intensity = 1.2): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: '#111111',
    emissive: new THREE.Color(color),
    emissiveIntensity: intensity,
    roughness: 0.4,
  });
}

// --- Tammy Lynn -------------------------------------------------------------------------------

function furnishTammy(kit: RoomKit, out: ParkHomeRoom['things'], puffs: THREE.Mesh[]): void {
  const S = PARK_HOME_SPOTS.tammy;
  const wall = mat('#f6dbe6', { rough: 0.9 });
  kit.add(couch('#ff9cc6'), 0.2, -1.25);
  kit.solid(0.2, -1.25, 2.0, 0.9);
  kit.add(rug(2.4, 1.2, '#f3b6cf'), 0.2, 0.2);
  // The inventory: boxes of oils to the ceiling, by the door so you see them first.
  const rng = new Random(505);
  const stock = new THREE.Group();
  for (let i = 0; i < 6; i++)
    for (let j = 0; j < 4 - (i % 2); j++) {
      const c = carton('WELLNESS™', 0.5, 0.36, 0.42, rng.pick(['#b08a5a', '#a87f50', '#bb9464']));
      c.position.set(-1.35 + i * 0.54, j * 0.37, rng.range(-0.03, 0.03));
      c.rotation.y = rng.range(-0.06, 0.06);
      stock.add(c);
    }
  out.stock = kit.add(stock, 4.1, -1.5);
  kit.solid(4.1, -1.5, 3.3, 0.55);
  // The diffuser on a side table, puffing away.
  const table = new THREE.Group();
  table.add(cyl(0.28, 0.28, 0.04, mat('#f4f4f0'), 0, 0.62, 0, 14));
  table.add(cyl(0.04, 0.04, 0.6, M.chrome, 0, 0.3, 0, 6));
  table.add(cyl(0.1, 0.13, 0.18, mat('#e9d7c3', { rough: 0.3 }), 0, 0.73, 0, 12));
  for (let i = 0; i < 3; i++) {
    // Each puff fades on its own, so each gets its own material.
    const mist = new THREE.MeshStandardMaterial({
      color: '#ffffff',
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
      roughness: 1,
    });
    const p = sphere(0.08, mist, 0, 0.9, 0, 8, { cast: false, receive: false });
    puffs.push(p);
    table.add(p);
  }
  out.diffuser = kit.place(table, S.diffuser.x, -1.4);
  kit.circle(S.diffuser.x, -1.4, 0.3);
  // The vision board over the couch, and the sign every trailer is required to have.
  const board = poster(
    {
      w: 512,
      h: 320,
      bg: '#fff3f8',
      fg: '#c2185b',
      border: '#c2185b',
      borderWidth: 10,
      gap: 4,
      lines: [
        { text: 'VISION BOARD', size: 52, font: 'Anton' },
        { text: ':yacht: :nails: :money-wings: :island:', size: 64, font: 'Anton' },
        { text: 'BOSS BABE • DEBT FREE BY 2019', size: 30, font: 'Anton', color: '#1b1b1b' },
      ],
    },
    1.3,
    0.8,
  );
  onBack(kit, board, S.board.x, 1.65, 0.02);
  out.board = board;
  onFront(
    kit,
    poster(
      {
        w: 512,
        h: 160,
        bg: '#f6dbe6',
        fg: '#7a4a5a',
        lines: [{ text: 'Live • Laugh • Love', size: 70, font: 'Pacifico' }],
      },
      1.2,
      0.38,
    ),
    1.75,
    1.85,
    0.02,
  );
  window_(kit, -1.0, '#ff9cc6');
  // Her ring light, for going live about the opportunity.
  const ring = new THREE.Group();
  for (const a of [0, (Math.PI * 2) / 3, (Math.PI * 4) / 3]) {
    const leg = cyl(0.012, 0.012, 1.45, M.black, Math.sin(a) * 0.18, 0.7, Math.cos(a) * 0.18, 4);
    leg.rotation.set(Math.cos(a) * 0.12, 0, -Math.sin(a) * 0.12);
    ring.add(leg);
  }
  const halo = mesh(new THREE.TorusGeometry(0.22, 0.03, 6, 20), glow('#fff6e8', 0.9), 0, 1.5, 0, {
    cast: false,
  });
  ring.add(halo);
  ring.add(box(0.07, 0.14, 0.01, M.black, 0, 1.5, 0.01));
  kit.place(ring, -1.8, 1.2, Math.PI + 0.4);
  kit.circle(-1.8, 1.2, 0.25);
  bedroom(kit, wall, '#ff6fa8');
  onBack(
    kit,
    poster(
      {
        w: 384,
        h: 160,
        bg: '#ffffff',
        fg: '#c2185b',
        lines: [{ text: '#blessed', size: 70, font: 'Pacifico' }],
      },
      0.9,
      0.38,
    ),
    -4.6,
    1.6,
    0.02,
  );
}

// --- Big Earl ---------------------------------------------------------------------------------

function furnishEarl(
  kit: RoomKit,
  out: ParkHomeRoom['things'],
  tvGlow: THREE.MeshStandardMaterial[],
): {
  bass: THREE.Mesh;
} {
  const S = PARK_HOME_SPOTS.earl;
  const wall = texMat(panelingTexture());
  out.recliner = kit.add(bigRecliner(), S.recliner.x, S.recliner.z);
  kit.solid(S.recliner.x, S.recliner.z, 1.0, 1.0);
  // NASCAR, always.
  const tv = crtTv();
  tvGlow.push(tv.screen);
  tv.screen.emissive.set('#9ad1ff');
  kit.place(tv.group, S.recliner.x, 1.45, Math.PI);
  kit.solid(S.recliner.x, 1.45, 1.0, 0.45);
  kit.add(cooler(), S.recliner.x + 0.95, -1.3);
  kit.solid(S.recliner.x + 0.95, -1.3, 0.7, 0.45);
  kit.add(rug(1.8, 1.4, '#4f5a33'), S.recliner.x, 0.1);
  // The beer fridge and a kitchenette, trophies on the counter.
  out.fridge = kit.add(homeFridge(), 5.2, -1.4);
  kit.solid(5.2, -1.4, 0.78, 0.7);
  kit.add(kitchenCounter(2.0, true), 3.6, -1.45);
  kit.solid(3.6, -1.45, 2.0, 0.65);
  const gold = mat('#d4a72c', { metal: 0.7, rough: 0.3 });
  for (let i = 0; i < 4; i++) {
    const t = new THREE.Group();
    const h = 0.18 + (i % 2) * 0.1;
    t.add(box(0.12, 0.05, 0.12, mat('#3a2a1a'), 0, 0.025, 0));
    t.add(cyl(0.02, 0.02, h, gold, 0, 0.05 + h / 2, 0, 6));
    t.add(cyl(0.08, 0.03, 0.1, gold, 0, 0.1 + h, 0, 10));
    // On the counter (its top is at 0.91).
    t.position.y = 0.91;
    kit.add(t, 2.85 + i * 0.22, -1.62);
  }
  onBack(
    kit,
    poster(
      {
        w: 512,
        h: 192,
        bg: '#2b2b2e',
        fg: '#ffd400',
        lines: [
          { text: 'TRI-COUNTY RIB OFF', size: 48, font: 'Anton' },
          { text: 'CHAMPION 1998 • 2003 • 2011 • 2019', size: 26, font: 'Anton', color: '#ffffff' },
        ],
      },
      1.4,
      0.52,
    ),
    3.6,
    1.75,
    0.02,
  );
  const deer = deerMount();
  onBack(kit, deer, S.recliner.x, 1.8);
  const b = singingBass();
  onBack(kit, b.group, S.bass.x, 1.45);
  out.bass = b.group;
  onFront(kit, neonBeerSign(), -1.0, 1.75);
  // Tank's bed, which Tank has never once slept in.
  kit.add(cyl(0.45, 0.45, 0.14, texMat(plaidTexture('#8a3a2a')), 0, 0.07, 0, 16), 2.3, 1.2);
  kit.add(cyl(0.1, 0.08, 0.06, mat('#c0392b'), 0, 0.03, 0, 10), 2.9, 1.5);
  bedroom(kit, wall, '#5e6b3a');
  onBack(kit, dogsPokerPainting(), -4.6, 1.65);
  return { bass: b.head };
}

// --- Cousin Dale ------------------------------------------------------------------------------

function furnishDale(kit: RoomKit, out: ParkHomeRoom['things']): void {
  const S = PARK_HOME_SPOTS.dale;
  const c = couch('#7a6a4a');
  out.cushions = kit.add(c, S.cushions.x, -1.25);
  kit.solid(S.cushions.x, -1.25, 2.0, 0.9);
  const tv = crtTv();
  tv.screen.emissiveIntensity = 0.05;
  kit.add(tv.group, S.cushions.x, 1.45, Math.PI);
  kit.solid(S.cushions.x, 1.45, 1.0, 0.45);
  // A cardboard window, from the inside.
  window_(kit, S.cushions.x, '#6b5a3a', mat('#9a7a4e', { rough: 1 }));
  // The business plan.
  const plan = new THREE.Group();
  plan.add(box(1.5, 0.95, 0.03, mat('#c9ced3', { metal: 0.3 }), 0, 0, 0));
  const writing = poster(
    {
      w: 512,
      h: 320,
      bg: '#f8f8f4',
      fg: '#1b4fb3',
      gap: 2,
      lines: [
        { text: 'BUSINESS PLAN', size: 54, font: 'Permanent Marker', color: '#c62828' },
        { text: '1. FIREWOOD', size: 44, font: 'Permanent Marker' },
        { text: '2. ???', size: 44, font: 'Permanent Marker' },
        { text: '3. YACHT', size: 44, font: 'Permanent Marker' },
      ],
    },
    1.4,
    0.86,
  );
  writing.position.z = 0.02;
  plan.add(writing);
  onBack(kit, plan, S.plans.x, 1.45);
  out.plans = plan;
  // Inventory. "Legal" inventory.
  const stock: Array<[string, number, number, number]> = [
    ['KNOCKOFF CROCS', 4.3, -1.45, 0],
    ['AS SEEN ON TV', 5.15, -1.45, 0],
    ['FIREWOOD (STATE PARK)', 4.7, -1.45, 0.42],
    ['PHONE CASES', 2.5, -1.45, 0],
  ];
  for (const [text, x, z, y] of stock) {
    const box_ = carton(text, 0.8, 0.42, 0.55);
    box_.position.y = y;
    kit.add(box_, x, z);
  }
  kit.solid(4.7, -1.45, 1.7, 0.6);
  kit.solid(2.5, -1.45, 0.85, 0.6);
  // A card table with a pyramid of empties on it.
  const table = new THREE.Group();
  table.add(box(0.8, 0.04, 0.8, mat('#3a3a3a'), 0, 0.72, 0));
  for (const x of [-0.36, 0.36])
    for (const z of [-0.36, 0.36]) table.add(box(0.03, 0.7, 0.03, M.steel, x, 0.35, z));
  const can = mat('#c9ced3', { metal: 0.7, rough: 0.3 });
  for (let row = 0; row < 4; row++)
    for (let i = 0; i < 4 - row; i++)
      table.add(
        cyl(0.033, 0.033, 0.12, can, (i - (3 - row) / 2) * 0.075, 0.8 + row * 0.122, 0, 8, { cast: false }),
      );
  kit.add(table, -3.0, 1.2);
  kit.solid(-3.0, 1.2, 0.8, 0.8);
  // No bedroom wall. It fell down. The mattress is the bedroom.
  kit.add(mattress(), -4.6, -0.9, Math.PI / 2);
  kit.solid(-4.6, -0.9, 1.4, 1.9);
  onFront(
    kit,
    poster(
      {
        w: 384,
        h: 288,
        bg: '#f4ecd2',
        fg: '#3a2a1a',
        border: '#c9a23a',
        borderWidth: 12,
        lines: [
          { text: 'ENTREPRENEUR', size: 46, font: 'Rye' },
          { text: 'OF THE YEAR', size: 40, font: 'Rye' },
          { text: '(SELF-AWARDED)', size: 28, font: 'Anton', color: '#8a2a2a' },
        ],
      },
      0.7,
      0.52,
    ),
    -1.6,
    1.6,
    0.02,
  );
}

// --- Old Man Jenkins --------------------------------------------------------------------------

function furnishJenkins(kit: RoomKit, out: ParkHomeRoom['things'], pendulum: THREE.Object3D[]): void {
  const S = PARK_HOME_SPOTS.jenkins;
  const wall = texMat(panelingTexture(), { color: '#c89a6a' });
  kit.add(recliner('#7d8a3a'), S.photos.x, -1.05);
  kit.solid(S.photos.x, -1.05, 0.95, 0.95);
  const tv = crtTv();
  tv.screen.emissive.set('#d8d8c8');
  tv.screen.emissiveIntensity = 0.25;
  kit.add(tv.group, S.photos.x, 1.45, Math.PI);
  kit.solid(S.photos.x, 1.45, 1.0, 0.45);
  kit.add(rug(2.0, 1.3, '#a0522d'), S.photos.x, 0.15);
  // The rotary phone, harvest gold, on its own little table.
  const phone = new THREE.Group();
  phone.add(box(0.45, 0.6, 0.4, texMat(woodTexture(), { color: '#8a6038' }), 0, 0.3, 0));
  phone.add(box(0.22, 0.09, 0.2, mat('#d4a33a', { rough: 0.4 }), 0, 0.65, 0));
  phone.add(box(0.24, 0.05, 0.06, mat('#d4a33a', { rough: 0.4 }), 0, 0.72, 0));
  kit.add(phone, 1.15, -1.45);
  kit.solid(1.15, -1.45, 0.45, 0.4);
  // Photos over the recliner: fifty years of Lucky Acres, in sepia.
  const photos = new THREE.Group();
  const rng = new Random(974);
  for (let i = 0; i < 7; i++) {
    const w = rng.range(0.22, 0.34);
    const h = rng.range(0.24, 0.32);
    const f = new THREE.Group();
    f.add(box(w + 0.05, h + 0.05, 0.02, mat('#5a3a1e'), 0, 0, 0));
    f.add(
      box(w, h, 0.01, mat(rng.pick(['#b8a07a', '#a89070', '#c8b088', '#9c8462'])), 0, 0, 0.012, {
        cast: false,
      }),
    );
    f.position.set(-0.8 + (i % 4) * 0.5 + (i >= 4 ? 0.25 : 0), i >= 4 ? -0.36 : 0, 0);
    photos.add(f);
  }
  onBack(kit, photos, S.photos.x, 1.9);
  out.photos = photos;
  // The newspaper clipping he's proudest of.
  onFront(
    kit,
    poster(
      {
        w: 384,
        h: 320,
        bg: '#efe6cf',
        fg: '#1b1b1b',
        border: '#5a3a1e',
        borderWidth: 12,
        gap: 4,
        lines: [
          { text: 'TRI-COUNTY GAZETTE', size: 30, font: 'Rye' },
          { text: 'TORNADO MOVES', size: 42, font: 'Anton' },
          { text: 'TRAILER 6 FEET', size: 42, font: 'Anton' },
          { text: '"IMPROVED THE VIEW," SAYS LOCAL MAN', size: 18, font: 'Anton', color: '#444' },
        ],
      },
      0.62,
      0.52,
    ),
    1.6,
    1.6,
    0.02,
  );
  // The grandfather clock: stopped at 4:17 since 1989.
  const clock = new THREE.Group();
  const wood = texMat(woodTexture(), { color: '#6b4226' });
  clock.add(box(0.5, 2.0, 0.34, wood, 0, 1.0, 0));
  const face = cyl(0.17, 0.17, 0.02, mat('#f4ecd2'), 0, 1.68, 0.175, 18, { cast: false });
  face.rotation.x = Math.PI / 2;
  clock.add(face);
  const hand = (len: number, rot: number): THREE.Mesh => {
    const m = box(0.015, len, 0.006, M.black, 0, 0, 0, { cast: false });
    m.geometry.translate(0, len / 2, 0);
    m.position.set(0, 1.68, 0.19);
    m.rotation.z = rot;
    return m;
  };
  clock.add(hand(0.1, -2.1), hand(0.14, -1.78));
  clock.add(box(0.3, 0.9, 0.02, mat('#2a1a10', { rough: 0.2 }), 0, 0.85, 0.172, { cast: false }));
  const swing = new THREE.Group();
  swing.position.set(0, 1.25, 0.185);
  swing.add(box(0.015, 0.5, 0.01, gold(), 0, -0.25, 0, { cast: false }));
  swing.add(cyl(0.06, 0.06, 0.012, gold(), 0, -0.52, 0, 14, { cast: false }).rotateX(Math.PI / 2));
  clock.add(swing);
  pendulum.push(swing);
  out.clock = kit.place(clock, S.clock.x, -1.55);
  kit.solid(S.clock.x, -1.55, 0.5, 0.36);
  // His kitchen, avocado green, untouched since Carter.
  const fridge = new THREE.Group();
  fridge.add(box(0.78, 1.65, 0.7, mat('#8a9a3a', { rough: 0.45 }), 0, 0.825, 0));
  fridge.add(box(0.04, 0.4, 0.05, M.chrome, -0.3, 1.2, 0.37, { cast: false }));
  kit.add(fridge, 5.2, -1.4);
  kit.solid(5.2, -1.4, 0.78, 0.7);
  kit.add(kitchenCounter(2.0, true), 3.6, -1.45);
  kit.solid(3.6, -1.45, 2.0, 0.65);
  window_(kit, 3.6, '#d9b45a');
  bedroom(kit, wall, '#d8c9a3');
  window_(kit, -4.6, '#d9b45a');
  // Her picture on the dresser.
  const frame = new THREE.Group();
  frame.add(box(0.2, 0.26, 0.02, gold(), 0, 0.13, 0));
  frame.add(box(0.15, 0.2, 0.01, mat('#b8a07a'), 0, 0.13, 0.012, { cast: false }));
  frame.rotation.x = -0.15;
  kit.add(frame, -3.4, 1.42, Math.PI);
}

function gold(): THREE.MeshStandardMaterial {
  return mat('#c9a23a', { metal: 0.7, rough: 0.3 });
}

// --- Skeeter ----------------------------------------------------------------------------------

function furnishSkeeter(
  kit: RoomKit,
  out: ParkHomeRoom['things'],
  rgb: THREE.MeshStandardMaterial[],
  screen: THREE.MeshStandardMaterial[],
): void {
  const S = PARK_HOME_SPOTS.skeeter;
  const { length: L, height: H } = kit.shape;
  // LED strips along the top of the walls, cycling colors like a gaming PC.
  const led = glow('#ff00ff', 1.4);
  rgb.push(led);
  onBack(kit, box(L - 0.2, 0.03, 0.02, led, 0, 0, 0, { cast: false }), 0, H - 0.12, 0.02);
  onFront(kit, box(L - 0.2, 0.03, 0.02, led, 0, 0, 0, { cast: false }), 0, H - 0.12, 0.02);
  // The desk, the monitor, the chair.
  const desk = new THREE.Group();
  desk.add(box(1.5, 0.05, 0.62, mat('#1b1b1f'), 0, 0.74, 0));
  for (const x of [-0.7, 0.7]) desk.add(box(0.05, 0.72, 0.58, mat('#1b1b1f'), x, 0.36, 0));
  desk.add(box(0.95, 0.55, 0.05, M.black, 0, 1.12, -0.15));
  const glass = glow('#4aa8ff', 0.9);
  screen.push(glass);
  desk.add(mesh(new THREE.PlaneGeometry(0.88, 0.48), glass, 0, 1.12, -0.12, { cast: false }));
  desk.add(box(0.5, 0.02, 0.16, mat('#2b2b2e'), 0, 0.775, 0.12, { cast: false }));
  for (const x of [0.55, 0.62])
    desk.add(cyl(0.033, 0.033, 0.13, mat('#39d353', { metal: 0.5, rough: 0.3 }), x, 0.83, 0.1, 8));
  out.upload = kit.place(desk, S.upload.x, -1.45);
  kit.solid(S.upload.x, -1.45, 1.5, 0.62);
  const chair = new THREE.Group();
  const red = mat('#c62828', { rough: 0.5 });
  chair.add(cyl(0.25, 0.28, 0.06, M.black, 0, 0.05, 0, 10));
  chair.add(cyl(0.04, 0.04, 0.4, M.chrome, 0, 0.27, 0, 6));
  chair.add(box(0.55, 0.1, 0.5, red, 0, 0.5, 0));
  chair.add(box(0.55, 0.85, 0.1, red, 0, 0.97, 0.24));
  chair.add(box(0.25, 0.85, 0.11, M.black, 0, 0.97, 0.24));
  kit.add(chair, S.upload.x, -0.75);
  kit.circle(S.upload.x, -0.75, 0.3);
  // The can pyramid, the pride of the place.
  const cans = new THREE.Group();
  const flavors = ['#39d353', '#2196f3', '#ff5722', '#e91e63'];
  for (let row = 0; row < 5; row++)
    for (let i = 0; i < 5 - row; i++)
      cans.add(
        cyl(
          0.034,
          0.034,
          0.13,
          mat(flavors[(row + i) % flavors.length] as string, { metal: 0.5, rough: 0.3 }),
          (i - (4 - row) / 2) * 0.075,
          0.065 + row * 0.132,
          0,
          8,
          { cast: false },
        ),
      );
  out.cans = kit.add(cans, S.cans.x, -1.55);
  kit.solid(S.cans.x, -1.55, 0.45, 0.2);
  // The homemade silver play button and the merch.
  const plaque = new THREE.Group();
  plaque.add(box(0.5, 0.6, 0.03, mat('#d5d8dc', { metal: 0.9, rough: 0.2 }), 0, 0, 0));
  const play = poster(
    {
      w: 256,
      h: 320,
      bg: '#d5d8dc',
      fg: '#c62828',
      gap: 4,
      lines: [
        { text: ':play:', size: 120, font: 'Anton' },
        { text: '3 SUBSCRIBERS', size: 30, font: 'Anton', color: '#1b1b1b' },
        { text: '(2 ARE MAMA)', size: 24, font: 'Anton', color: '#555' },
      ],
    },
    0.42,
    0.52,
  );
  play.position.z = 0.02;
  plaque.add(play);
  onBack(kit, plaque, 2.0, 1.6);
  onBack(
    kit,
    poster(
      {
        w: 512,
        h: 192,
        bg: '#c62828',
        fg: '#ffffff',
        lines: [
          { text: 'SKEETER SENDS IT', size: 64, font: 'Bangers' },
          { text: 'SMASH THAT LIKE', size: 36, font: 'Anton', color: '#ffd400' },
        ],
      },
      1.2,
      0.45,
    ),
    -0.9,
    1.7,
    0.02,
  );
  // His BMX, leaning on the wall by the door. It has never been outside a ditch.
  const bmx = bike('#ff2d55');
  bmx.rotation.x = -0.1;
  kit.add(bmx, 4.9, -1.5);
  kit.solid(4.9, -1.5, 1.4, 0.35);
  // Fireworks crates and a gamer's bedroom: a mattress and a flag for a curtain.
  for (let i = 0; i < 3; i++) {
    const c = carton(i === 1 ? 'DO NOT LIGHT' : 'BOOM', 0.6, 0.38, 0.45, '#b52a2a');
    c.position.y = i === 2 ? 0.38 : 0;
    kit.add(c, -3.0 + (i === 2 ? 0.3 : i * 0.65), 1.45);
  }
  kit.solid(-2.7, 1.45, 1.3, 0.5);
  const wall = mat('#3d4058', { rough: 0.9 });
  kit.add(partition(1.6, wall), -2.4, -1.0);
  kit.solid(-2.4, -1.0, 0.12, 1.6);
  kit.add(mattress(), -4.6, -0.9, Math.PI / 2);
  kit.solid(-4.6, -0.9, 1.4, 1.9);
  onBack(kit, label('NO CAP', '#111111', '#39d353', 0.8, 0.4, 70), -4.6, 1.5, 0.02);
}

// --- Meemaw Pearl -----------------------------------------------------------------------------

function furnishMeemaw(kit: RoomKit, out: ParkHomeRoom['things'], heads: THREE.Object3D[]): void {
  const S = PARK_HOME_SPOTS.meemaw;
  const wall = mat('#f3e3c8', { rough: 0.9 });
  // The good couch, in its plastic. Nobody has sat on it since 1996.
  kit.add(couch('#c75b6b'), 0.3, -1.25);
  kit.add(
    box(2.16, 0.95, 0.96, mat('#e8f4ff', { transparent: true, opacity: 0.22, rough: 0.05 }), 0, 0.47, 0, {
      cast: false,
    }),
    0.3,
    -1.25,
  );
  kit.solid(0.3, -1.25, 2.16, 0.96);
  kit.add(rug(2.2, 1.2, '#d8c3e0'), 0.3, 0.15);
  window_(kit, 0.3, '#fbf6ec');
  // The candy dish, on a doily, on a table that is also a doily.
  const table = new THREE.Group();
  table.add(cyl(0.3, 0.3, 0.04, texMat(woodTexture(), { color: '#8a5a33' }), 0, 0.62, 0, 16));
  table.add(cyl(0.05, 0.08, 0.6, texMat(woodTexture(), { color: '#8a5a33' }), 0, 0.3, 0, 8));
  table.add(cyl(0.26, 0.26, 0.006, mat('#fbf8f0'), 0, 0.643, 0, 16, { cast: false }));
  table.add(
    cyl(0.13, 0.08, 0.07, mat('#d4a33a', { transparent: true, opacity: 0.55, rough: 0.1 }), 0, 0.68, 0, 14),
  );
  const candy = ['#d4a33a', '#c62828', '#f4f4f0', '#2a9d8f'];
  for (let i = 0; i < 9; i++)
    table.add(
      sphere(
        0.022,
        mat(candy[i % candy.length] as string, { rough: 0.2 }),
        Math.cos(i * 2.4) * 0.07,
        0.715,
        Math.sin(i * 2.4) * 0.07,
        6,
        {
          cast: false,
        },
      ),
    );
  out.candy = kit.add(table, S.candy.x, -1.4);
  kit.circle(S.candy.x, -1.4, 0.32);
  // The porcelain dolls, on a shelf, watching.
  const shelf = new THREE.Group();
  shelf.add(box(1.4, 0.04, 0.3, texMat(woodTexture(), { color: '#8a5a33' }), 0, 0, 0));
  const dress = ['#f3d9e6', '#c9e4f6', '#fff2c8', '#e0d0f0'];
  for (let i = 0; i < 4; i++) {
    const doll = new THREE.Group();
    doll.add(cyl(0.02, 0.09, 0.2, mat(dress[i] as string), 0, 0.12, 0, 10));
    const head = new THREE.Group();
    head.position.y = 0.27;
    head.add(sphere(0.06, mat('#f8ece4', { rough: 0.2 }), 0, 0, 0, 10));
    const hair = sphere(0.064, mat(i % 2 ? '#e8c060' : '#5b3a1e'), 0, 0.02, -0.014, 10);
    hair.scale.set(1, 0.9, 1);
    head.add(hair);
    for (const s of [-1, 1])
      head.add(box(0.012, 0.012, 0.01, M.black, s * 0.022, 0.005, 0.056, { cast: false }));
    heads.push(head);
    doll.add(head);
    doll.position.set(-0.5 + i * 0.33, 0.02, 0);
    shelf.add(doll);
  }
  shelf.position.y = 1.35;
  out.dolls = kit.place(shelf, S.dolls.x, BACK + 0.17);
  // Grandbabies: fourteen of them, she thinks.
  const kids = new THREE.Group();
  const rng = new Random(1414);
  for (let i = 0; i < 14; i++) {
    const f = new THREE.Group();
    f.add(box(0.2, 0.25, 0.02, gold(), 0, 0, 0));
    f.add(
      box(0.15, 0.2, 0.01, mat(rng.pick(['#c9e4f6', '#f3d9e6', '#d8e8c0', '#fff2c8'])), 0, 0, 0.012, {
        cast: false,
      }),
    );
    f.position.set(-1.05 + (i % 7) * 0.35, i < 7 ? 0.3 : 0, 0);
    kids.add(f);
  }
  // On the end wall by the door, where you can't miss them.
  kids.position.set(kit.shape.length / 2 - 0.03, 1.5, S.grandkids.z);
  kids.rotation.y = -Math.PI / 2;
  kit.walls.east.add(kids);
  out.grandkids = kids;
  // Her kitchen, and a cake under glass that is not for you.
  kit.add(kitchenCounter(2.4, true), 4.4, -1.45);
  kit.solid(4.4, -1.45, 2.4, 0.65);
  const cake = new THREE.Group();
  cake.add(cyl(0.16, 0.16, 0.12, mat('#f3d9e6'), 0, 0.97, 0, 14));
  cake.add(
    sphere(0.2, mat('#e8f4ff', { transparent: true, opacity: 0.25, rough: 0.05 }), 0, 0.95, 0, 14, {
      cast: false,
    }),
  );
  kit.add(cake, 5.25, -1.5);
  bedroom(kit, wall, '#a8346b');
  // Elvis, on black velvet, over the bed.
  onBack(
    kit,
    poster(
      {
        w: 256,
        h: 320,
        bg: '#0d0d14',
        fg: '#e8c070',
        gap: 4,
        lines: [
          { text: ':crown:', size: 110, font: 'Rye' },
          { text: 'THE KING', size: 52, font: 'Rye' },
        ],
      },
      0.62,
      0.78,
    ),
    -4.6,
    1.6,
    0.02,
  );
}

// ----------------------------------------------------------------------------------------------

/** Builds one neighbor's trailer interior. */
export function buildParkHome(
  id: ParkHomeId,
  parent: THREE.Object3D,
  collision: CollisionWorld,
): ParkHomeRoom {
  const things: ParkHomeRoom['things'] = {};
  const pokes: Partial<Record<string, () => void>> = {};
  const finish = {
    tammy: {
      wall: mat('#f6dbe6', { rough: 0.9 }),
      floor: texMat(shagTexture('#e8c8d8'), { rough: 1 }),
      ceiling: mat('#fbf3f6'),
    },
    earl: {
      wall: texMat(panelingTexture()),
      floor: texMat(shagTexture('#7a5a3a'), { rough: 1 }),
      ceiling: mat('#e8e0cc'),
    },
    dale: {
      wall: mat('#bfae8a', { rough: 1 }),
      floor: texMat(linoleumTexture(), { color: '#b8a888' }),
      ceiling: mat('#d8ccb0'),
    },
    jenkins: {
      wall: texMat(panelingTexture(), { color: '#c89a6a' }),
      floor: texMat(shagTexture('#c76b1e'), { rough: 1 }),
      ceiling: mat('#efe6cf'),
    },
    skeeter: {
      wall: mat('#3d4058', { rough: 0.9 }),
      floor: texMat(shagTexture('#3a3a48'), { rough: 1 }),
      ceiling: mat('#22232c'),
    },
    meemaw: {
      wall: mat('#f3e3c8', { rough: 0.9 }),
      floor: texMat(shagTexture('#b9a7c9'), { rough: 1 }),
      ceiling: mat('#fbf6ec'),
    },
  }[id];
  const room = new PublicRoom(parent, collision, PARK_HOMES[id].slot, 'single', finish, (kit) => {
    switch (id) {
      case 'tammy': {
        const puffs: THREE.Mesh[] = [];
        furnishTammy(kit, things, puffs);
        kit.animate((time) =>
          puffs.forEach((p, i) => {
            const k = (time * 0.35 + i / puffs.length) % 1;
            p.position.y = 0.88 + k * 0.6;
            p.scale.setScalar(0.6 + k * 2.2);
            (p.material as THREE.MeshStandardMaterial).opacity = 0.35 * (1 - k);
          }),
        );
        break;
      }
      case 'earl': {
        const tv: THREE.MeshStandardMaterial[] = [];
        const { bass } = furnishEarl(kit, things, tv);
        let sing = 0;
        pokes.bass = () => (sing = 3);
        kit.animate((time, dt) => {
          sing = Math.max(0, sing - dt);
          bass.rotation.y = sing > 0 ? Math.sin(time * 14) * 0.35 : 0;
          // Cars going by on the TV, left, left, left.
          for (const s of tv) s.emissiveIntensity = 0.45 + 0.15 * Math.sin(time * 9) * Math.sin(time * 2.3);
        });
        break;
      }
      case 'dale':
        furnishDale(kit, things);
        break;
      case 'jenkins': {
        const pendulum: THREE.Object3D[] = [];
        furnishJenkins(kit, things, pendulum);
        let wound = 0;
        pokes.clock = () => (wound = 30);
        kit.animate((time, dt) => {
          wound = Math.max(0, wound - dt);
          const swing = wound > 0 ? Math.sin(time * 3.2) * 0.22 * Math.min(1, wound / 5) : 0;
          for (const p of pendulum) p.rotation.z = swing;
        });
        break;
      }
      case 'skeeter': {
        const rgb: THREE.MeshStandardMaterial[] = [];
        const screen: THREE.MeshStandardMaterial[] = [];
        furnishSkeeter(kit, things, rgb, screen);
        let flash = 0;
        pokes.upload = () => (flash = 6);
        kit.animate((time, dt) => {
          flash = Math.max(0, flash - dt);
          for (const m of rgb) m.emissive.setHSL((time * 0.08) % 1, 1, 0.5);
          for (const m of screen) {
            m.emissive.setHSL(flash > 0 ? (time * 0.9) % 1 : 0.58, flash > 0 ? 0.9 : 0.7, 0.55);
            m.emissiveIntensity = 0.8 + (flash > 0 ? 0.4 * Math.sin(time * 20) : 0);
          }
        });
        break;
      }
      case 'meemaw': {
        const heads: THREE.Object3D[] = [];
        furnishMeemaw(kit, things, heads);
        // The dolls' heads turn. Slowly. You never see them do it.
        kit.animate((time) =>
          heads.forEach((h, i) => (h.rotation.y = Math.sin(time * 0.21 + i * 1.7) * 0.7)),
        );
        break;
      }
    }
  });
  return { room, things, poke: (what) => pokes[what]?.() };
}
